/* eslint-disable react-native/no-inline-styles */
// NOTE: This component is used on *WEB* only!

import { ProductPanelScrollMarker } from '@Components/ProductDetailPanel/product-panel-scroll-marker';
import { useSideDrawerLeftPresenter } from '@Components/SideDrawerLeft/SideDrawerLeftPresenter';
import { IS_SMALL_SCREEN } from '@Constants/LeftDrawer';
import useCheckoutPresenter from '@Screens/Checkout/presenter';
import { observer } from 'mobx-react-lite';
import type { FC } from 'react';
import React, { useEffect, useRef } from 'react';
import { TouchableOpacity } from 'react-native';
import type { GroupTypeBase, OptionTypeBase, Styles } from 'react-select';
import styled from 'styled-components/native';

import OwnableTheme from '../../../../../assets/OwnableTheme';
import { Switch } from '../../../../Common/Switch';
import { Text } from '../../../../Common/Text';
import TextInput from '../../../../Common/TextInput';
import GooglePlacesInput from '../../../../GoogleInput';
import { defaultInputProps } from '../index';

import { useCheckoutStore } from '~stores/checkout';

const StyledBillingDetailsContainer = styled.View`
  margin-top: 24px;
  margin-bottom: 20px;
  width: 100%;
  border-radius: 27px;
  background-color: ${() => OwnableTheme.colors.white};
  padding: ${IS_SMALL_SCREEN ? '25px 25px' : '35px 40px'};
`;

const StyledInputLabel = styled(Text)`
  width: 100%;
  margin-left: 10px;
`;

const StyledCheckbox = styled.View`
  flex-direction: row;
  align-items: center;
  margin: 20px 0 30px 0;
`;

const StyledInputContainer = styled.View`
  overflow: hidden;
`;

const StyledSwitchContainer = styled.View`
  margin-right: 10px;
  padding-left: 3px;
`;

type Props = {
  checkoutIsSubmitting: boolean;
};

export const defaultAddressInputStyle = (): Partial<
  Styles<OptionTypeBase, false, GroupTypeBase<OptionTypeBase>>
> => ({
  placeholder: () => ({
    color: OwnableTheme.colors.gray,
    textAlign: 'left',
    fontFamily: OwnableTheme.fonts.gilroy.regular,
    fontSize: OwnableTheme.fontSizesNumerical.xs,
    width: '100%',
    position: 'absolute',
  }),
  loadingMessage: () => ({
    fontFamily: OwnableTheme.fonts.gilroy.regular,
    fontSize: OwnableTheme.fontSizesNumerical.xs,
    color: OwnableTheme.colors.gray,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  }),
  noOptionsMessage: () => ({
    fontFamily: OwnableTheme.fonts.gilroy.regular,
    fontSize: OwnableTheme.fontSizesNumerical.xs,
    color: OwnableTheme.colors.black,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  }),
  option: () => ({
    fontFamily: OwnableTheme.fonts.gilroy.regular,
    marginBottom: 3,
    paddingLeft: 5,
    color: OwnableTheme.colors.black,
    fontSize: OwnableTheme.fontSizesNumerical.xs,
  }),
  input: () => ({
    fontFamily: OwnableTheme.fonts.gilroy.regular,
    color: OwnableTheme.colors.black,
    backgroundColor: OwnableTheme.colors.white,
    fontSize: OwnableTheme.fontSizesNumerical.xs,
    // Manually set the position so that it overlays the placeholder
    // Without this, the height of the box adjusts if an item is selected and then the user starts typing
    position: 'absolute',
    left: 6,
    top: 4,
  }),
  control: () => ({
    fontFamily: OwnableTheme.fonts.gilroy.regular,
    color: OwnableTheme.colors.black,
    backgroundColor: OwnableTheme.colors.white,
    flexDirection: 'row',
    justifyContent: 'space-between',
    display: 'flex',
    textAlign: 'left',
    paddingTop: 7,
    paddingBottom: 7,
    paddingLeft: 13,
  }),
  container: () => ({
    fontFamily: OwnableTheme.fonts.gilroy.regular,
    color: OwnableTheme.colors.white,
    borderRadius: 999,
    backgroundColor: OwnableTheme.colors.white,
    paddingVertical: 10,
  }),
  singleValue: () => ({
    fontSize: OwnableTheme.fontSizesNumerical.xs,
    color: OwnableTheme.colors.black,
    opacity: 0.5,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  }),
});

const CollectDeliveryAddress: FC<Props> = ({ checkoutIsSubmitting }) => {
  const {
    checkoutDeliveryAddress,
    setCheckoutDeliveryAddressField,
    isFeatureAvailableForCheckout,
    authedUserSaveNewAddress,
    authedUserSetNewAddressAsDefault,
    setAuthedUserSaveNewAddress,
    setAuthedUserSetNewAddressAsDefault,
    _onCancelCheckoutButton,
  } = useCheckoutStore();

  const { activeProductId: productId } = useSideDrawerLeftPresenter();

  const previousProductId = useRef(productId);

  useEffect(() => {
    if (productId !== previousProductId.current) {
      _onCancelCheckoutButton();
      previousProductId.current = productId;
    }
  }, [productId]);

  const { getDeliveryAddress, processOrderViaSingletonClass, getBorderColor } =
    useCheckoutPresenter();

  return (
    <>
      <ProductPanelScrollMarker autoFocus />
      <StyledBillingDetailsContainer>
        <TextInput
          {...defaultInputProps}
          value={checkoutDeliveryAddress?.recipientName ?? ''}
          textAlign='left'
          placeholder='Name'
          onChange={(text) => {
            setCheckoutDeliveryAddressField('recipientName', text);
            processOrderViaSingletonClass().then();
          }}
          borderBottomColor={getBorderColor('DELIVERY_RECIPIENT_NAME')}
          editable={!checkoutIsSubmitting}
          id='fname'
          name='fname'
          autocomplete='name'
        />

        <GooglePlacesInput
          onSelect={async (address) => {
            await getDeliveryAddress(address);
          }}
          styles={defaultAddressInputStyle()}
        />

        <TextInput
          {...defaultInputProps}
          value={checkoutDeliveryAddress?.street ?? ''}
          textAlign='left'
          placeholder='Street'
          onChange={(text) => {
            setCheckoutDeliveryAddressField('street', text);
            processOrderViaSingletonClass().then();
          }}
          borderBottomColor={getBorderColor('DELIVERY_STREET')}
          editable={!checkoutIsSubmitting}
          id='ship-address'
          name='ship-address'
          autocomplete='street-address'
        />

        <TextInput
          {...defaultInputProps}
          value={checkoutDeliveryAddress?.townOrCity ?? ''}
          placeholder='Town / City'
          textAlign='left'
          onChange={(text) => {
            setCheckoutDeliveryAddressField('townOrCity', text);
            processOrderViaSingletonClass().then();
          }}
          borderBottomColor={getBorderColor('DELIVERY_TOWN')}
          editable={!checkoutIsSubmitting}
          id='ship-city'
          name='ship-city'
          autocomplete='address-level2'
        />

        <TextInput
          {...defaultInputProps}
          value={checkoutDeliveryAddress?.county ?? ''}
          placeholder='County'
          textAlign='left'
          onChange={(text) => {
            setCheckoutDeliveryAddressField('county', text);
            processOrderViaSingletonClass().then();
          }}
          editable={!checkoutIsSubmitting}
          id='state'
          name='state'
          autocomplete='address-level1'
        />

        <TextInput
          {...defaultInputProps}
          value={checkoutDeliveryAddress?.postcode ?? ''}
          placeholder='Postcode'
          textAlign='left'
          onChange={(text) => {
            setCheckoutDeliveryAddressField('postcode', text);
            processOrderViaSingletonClass().then();
          }}
          borderBottomColor={getBorderColor('DELIVERY_POSTCODE')}
          editable={!checkoutIsSubmitting}
          id='ship-zip'
          name='ship-zip'
          autocomplete='postal-code'
        />

        <TextInput
          {...defaultInputProps}
          textAlign='left'
          value={checkoutDeliveryAddress?.phone ?? ''}
          placeholder='Phone number'
          onChange={(text) => {
            setCheckoutDeliveryAddressField('phone', text);
            processOrderViaSingletonClass().then();
          }}
          borderBottomColor={getBorderColor('DELIVERY_PHONE_NUMBER')}
          editable={!checkoutIsSubmitting}
          id='phone'
          name='phone'
          autocomplete='tel'
        />
        {isFeatureAvailableForCheckout('AddNewAddress') && (
          <>
            <StyledCheckbox>
              <StyledSwitchContainer>
                <Switch
                  color='black'
                  isSwitchOn={authedUserSaveNewAddress}
                  onChange={() => {
                    setAuthedUserSaveNewAddress(!authedUserSaveNewAddress);
                  }}
                />
              </StyledSwitchContainer>
              <TouchableOpacity
                activeOpacity={1}
                onPress={() => {
                  setAuthedUserSaveNewAddress(!authedUserSaveNewAddress);
                }}
              >
                <StyledInputLabel
                  size='s'
                  weight='bold'
                  align='left'
                  color='black'
                >
                  Save address
                </StyledInputLabel>
              </TouchableOpacity>
            </StyledCheckbox>
            {authedUserSaveNewAddress && (
              <StyledInputContainer>
                <TextInput
                  {...defaultInputProps}
                  textAlign='left'
                  value={checkoutDeliveryAddress.displayName}
                  placeholder="Address name e.g: 'Home'"
                  onChange={(text) => {
                    setCheckoutDeliveryAddressField('displayName', text);
                    processOrderViaSingletonClass().then();
                  }}
                  borderBottomColor={getBorderColor('DELIVERY_DISPLAY_NAME')}
                  editable={authedUserSaveNewAddress}
                />

                <StyledCheckbox>
                  <StyledSwitchContainer>
                    <Switch
                      color='black'
                      isSwitchOn={authedUserSetNewAddressAsDefault}
                      onChange={() => {
                        setAuthedUserSetNewAddressAsDefault(
                          !authedUserSetNewAddressAsDefault
                        );
                      }}
                    />
                  </StyledSwitchContainer>

                  <TouchableOpacity
                    activeOpacity={1}
                    onPress={() => {
                      setAuthedUserSetNewAddressAsDefault(
                        !authedUserSetNewAddressAsDefault
                      );
                    }}
                  >
                    <StyledInputLabel
                      size='s'
                      weight='bold'
                      align='left'
                      color='black'
                    >
                      Make default address
                    </StyledInputLabel>
                  </TouchableOpacity>
                </StyledCheckbox>
              </StyledInputContainer>
            )}
          </>
        )}
      </StyledBillingDetailsContainer>
    </>
  );
};

export default observer(CollectDeliveryAddress);
